import { useState } from "react";
import { toast } from "sonner";
import { MessageSquarePlus, CornerUpLeft } from "lucide-react";
import { api, errMsg, fmtDate } from "../lib/api";
import { Panel, btnPrimary, inputCls } from "./ui-kit";
import { VoicePlayer, VoiceRecorder, feedbackForm } from "./Voice";

export const FeedbackComposer = ({ task, onChange }) => {
  const [text, setText] = useState("");
  const [notes, setNotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !notes.length) return toast.error("Write feedback or record a voice note");
    setBusy(true);
    try {
      onChange((await api.post(`/tasks/${task.id}/feedback`, feedbackForm(text, notes))).data);
      notes.forEach((n) => URL.revokeObjectURL(n.url));
      setText(""); setNotes([]);
      toast.success("Feedback added");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mb-6 space-y-2 rounded-xl border border-[#282E3D] bg-[#0B0D12]/60 p-4" data-testid="feedback-composer">
      <div className="flex items-center gap-2 text-sm font-semibold"><MessageSquarePlus className="h-4 w-4 text-[#FF6B00]" />Add feedback</div>
      <textarea rows={2} data-testid="feedback-text-input" placeholder="Write feedback, or record a voice note below…" className={inputCls} value={text} onChange={(e) => setText(e.target.value)} />
      <VoiceRecorder notes={notes} setNotes={setNotes} prefix="feedback-voice" />
      <button data-testid="feedback-submit-button" disabled={busy} className={`${btnPrimary} w-full`}>{busy ? "Posting…" : "Post feedback"}</button>
    </form>
  );
};

export const FeedbackBody = ({ task, h }) => (
  <>
    {h.comment && <div className={`mt-1.5 whitespace-pre-wrap rounded-lg border px-3 py-2 text-sm ${h.action === "sent_back" ? "border-rose-500/30 bg-rose-500/5 text-rose-100" : h.action === "feedback" ? "border-[#FF6B00]/30 bg-[#FF6B00]/5 text-slate-100" : "border-[#282E3D] bg-[#0B0D12] text-slate-300"}`}>{h.comment}</div>}
    {h.voice_notes?.length > 0 && <div className="mt-1.5 space-y-1.5">{h.voice_notes.map((v) => <VoicePlayer key={v.id} taskId={task.id} note={v} />)}</div>}
  </>
);

// Feedback since the receiving step last handed off, up to the latest send-back.
const pendingFeedback = (task) => {
  const hist = task.history;
  const sbIdx = hist.map((h) => h.action).lastIndexOf("sent_back");
  if (sbIdx < 0) return [];
  const sb = hist[sbIdx];
  let from = 0;
  for (let i = sbIdx - 1; i >= 0; i--) {
    if (hist[i].action === "completed_step" && hist[i].step_index === sb.to_step) { from = i + 1; break; }
  }
  return hist.slice(from, sbIdx + 1).filter((h) => (h.action === "feedback" || h.action === "sent_back") && (h.comment || h.voice_notes?.length));
};

export const FeedbackToAddress = ({ task }) => {
  const sbIdx = task.history.map((h) => h.action).lastIndexOf("sent_back");
  if (task.status === "completed" || sbIdx < 0 || task.history.slice(sbIdx).some((h) => h.action === "completed_step")) return null;
  const last = task.history[task.history.length - 1];
  const items = pendingFeedback(task);
  const sb = task.history[sbIdx];
  return (
    <Panel className="border-rose-500/40 bg-rose-500/[0.04] p-6" data-testid="feedback-to-address-panel">
      <div className="flex items-center gap-2 font-heading text-lg font-semibold text-rose-200"><CornerUpLeft className="h-4 w-4" />Sent back: feedback to address</div>
      <p className="mt-1 text-xs text-slate-400">{sb.user_name} sent this back to "{task.steps[sb.to_step]?.name}" · {fmtDate(sb.at)}{last.action === "sent_back" ? "" : " · being reworked"}</p>
      <div className="mt-4 space-y-4" data-testid="feedback-to-address-list">
        {items.map((h) => (
          <div key={h.id} data-testid={`feedback-item-${h.id}`}>
            <div className="text-xs"><b className="text-slate-100">{h.user_name}</b> <span className="text-slate-500">{h.action === "sent_back" ? "· with send-back" : "· feedback"} · {fmtDate(h.at)}</span></div>
            <FeedbackBody task={task} h={h} />
          </div>
        ))}
      </div>
    </Panel>
  );
};
